import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '../../lib/cn';

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'premium' | 'flat';
}

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant = 'default', ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'transition-all duration-450 ease-stripe',
        variant === 'default' &&
          'rounded-3xl border border-stone-200/70 bg-surface shadow-subtle hover:shadow-medium hover:-translate-y-0.5',
        variant === 'premium' && 'premium-card rounded-3xl border border-stone-200/70 bg-surface shadow-premium hover:-translate-y-1',
        variant === 'flat' && 'rounded-3xl bg-ivory-100 border border-ivory-200',
        className,
      )}
      {...props}
    />
  ),
);
Card.displayName = 'Card';

export const CardHeader = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-1.5 p-6 md:p-8', className)} {...props} />
);

export const CardTitle = ({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) => (
  // Polymorphic heading wrapper: children are provided by consumers at call sites.
  // eslint-disable-next-line jsx-a11y/heading-has-content
  <h3
    className={cn(
      'font-display text-xl font-semibold leading-tight tracking-tight text-ink',
      className,
    )}
    {...props}
  />
);

export const CardDescription = ({ className, ...props }: HTMLAttributes<HTMLParagraphElement>) => (
  <p className={cn('text-sm text-stone-600 leading-relaxed', className)} {...props} />
);

export const CardContent = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('p-6 md:p-8 pt-0', className)} {...props} />
);

export const CardFooter = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex items-center p-6 md:p-8 pt-0', className)} {...props} />
);
